import { AlertSeverityStoreModelEnum, AlertStateStoreModelEnum } from '@domain/alerting/alerting.enums';
import { AlertDtoStoreModel } from '@domain/alerting/alerting.models';

import { AlertingCardTableDataSourceViewModel, AlertingCardTableViewModel } from '@shared/components/alerting-card-table';
import { AlertingTableSortPropertyStoreModelEnum } from '@shared/enums/alerting-table-sort-property.enum';
import { SortDirectionStoreModelEnum } from '@shared/enums/sort-direction.enum';
import { compareByDateString, compareBySeverity, compareByString } from '@shared/utils/sort-utils';

export interface AlertingCardTableFilter {
  severity?: AlertSeverityStoreModelEnum;
  state?: AlertStateStoreModelEnum;
}

export const mapAlertsToAlertingCardTableViewModel = (
  alerts: AlertDtoStoreModel[] | undefined,
  dependenciesAreLoading: boolean | undefined,
  filter: AlertingCardTableFilter,
  sortProperty: AlertingTableSortPropertyStoreModelEnum,
  sortDirection: SortDirectionStoreModelEnum,
  sites?: { id: string; name: string }[]
): AlertingCardTableViewModel | undefined => {
  if (!alerts && !dependenciesAreLoading) {
    return;
  }

  let dataSource = alerts
    ?.filter((alert) => (!filter.severity || alert.severity === filter.severity) && (!filter.state || alert.state === filter.state))
    .map(
      (alert): AlertingCardTableDataSourceViewModel => ({
        id: alert.id,
        siteId: alert.siteId,
        siteName: sites?.find((site) => site.id === alert.siteId)?.name,
        type: alert.type,
        subject: alert.subject,
        chargerId: alert.chargerId,
        chargerName: alert.chargerName,
        evseName: alert.evseName,
        timestamp: alert.timestamp,
        sopUrl: alert.sopUrl,
        severity: alert.severity,
        dismissedReason: alert.dismissedReason,
      })
    );

  dataSource = dataSource && sortCardTableDataSource(dataSource, sortProperty, sortDirection);

  return {
    dataSource,
    isLoading: !!dependenciesAreLoading,
    sortProperty,
    sortDirection,
  };
};

const sortCardTableDataSource = (
  dataSource: AlertingCardTableDataSourceViewModel[],
  sortProperty: AlertingTableSortPropertyStoreModelEnum,
  sortDirection: SortDirectionStoreModelEnum
): AlertingCardTableDataSourceViewModel[] => {
  return dataSource.sort((a, b) => {
    switch (sortProperty) {
      case AlertingTableSortPropertyStoreModelEnum.Site: {
        return compareByString(a.siteName, b.siteName, sortDirection);
      }
      case AlertingTableSortPropertyStoreModelEnum.Type: {
        return compareByString(a.type, b.type, sortDirection);
      }
      case AlertingTableSortPropertyStoreModelEnum.Subject: {
        const aLabel = a.chargerName ? (a.evseName ? `${a.chargerName}/${a.evseName}` : a.chargerName) : a.subject;
        const bLabel = b.chargerName ? (b.evseName ? `${b.chargerName}/${b.evseName}` : b.chargerName) : b.subject;
        return compareByString(aLabel, bLabel, sortDirection);
      }
      case AlertingTableSortPropertyStoreModelEnum.Severity: {
        return compareBySeverity(a.severity, b.severity, sortDirection);
      }
      default: {
        return compareByDateString(a.timestamp, b.timestamp, sortDirection);
      }
    }
  });
};
